import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';
import { useOnWarehouseChange } from '~/hooks/useOnWarehouseChange';
import { catalogStore } from '~/stores';
import { Category } from '~/stores/CategoriesStore';

import CategoryBlockTitle from '../CategoryBlockTitle';
import { cfSrcForImage } from '../utils';

import styles from './CategoriesGridTwoMBlock.module.scss';

interface CategoriesGridTwoMBlockProps {
  category: Category;
}

const CategoriesGridTwoMBlock = ({
  category,
}: CategoriesGridTwoMBlockProps) => {
  const { handleClickCategory } = useCategoryAnalytics();

  const { isMobile } = useGlobal();

  useOnWarehouseChange((_, isChanged) => {
    if (isChanged) {
      category.productsRequest.run();
    }
  });

  if (!category.subcategory.length || !category.subcategory[0]?.id) {
    return null;
  }

  const getLinkProps = (to: string, onClick: () => void) => {
    return {
      state: { clean_filters: true },
      to,
      className: styles.link,
      onClick: () => {
        catalogStore.resetCatalogFilters();
        onClick();
      },
    };
  };

  return (
    <div className={styles.root}>
      {category.subcategory.map(({ id, name, subcategory }) => {
        return (
          <div
            key={id}
            className={styles.container}
            data-company={company.name}
          >
            <CategoryBlockTitle text={name} />
            {subcategory.length > 0 ? (
              <div className={styles.tiles}>
                {subcategory.map(
                  ({
                    name,
                    previewImage,
                    id,
                    parentId,
                    numberId,
                    tileSize,
                  }) => {
                    if (!parentId) {
                      return null;
                    }

                    const linkProps = getLinkProps(`/c/${id}`, () => {
                      handleClickCategory(numberId || parentId)();
                    });

                    const tileClassName = tileSize
                      ? TILE_SIZE_CLASS_NAMES[tileSize]
                      : undefined;

                    return (
                      <div
                        key={name}
                        className={classes([
                          styles.tile,
                          company.tileSizeEnabled
                            ? tileClassName
                            : styles.tile__x1,
                        ])}
                      >
                        <Link
                          {...linkProps}
                          style={{
                            backgroundImage: `var(--tileMask), url('${cfSrcForImage(
                              previewImage,
                              isMobile
                                ? { width: 360, height: 315 }
                                : { width: 624, height: 546 },
                            )}')`,
                          }}
                        >
                          <div className={styles.title}>{name}</div>
                        </Link>
                      </div>
                    );
                  },
                )}
              </div>
            ) : (
              <Skeleton
                count={3}
                className={styles.tile}
                containerClassName={styles.tiles}
                inline={true}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const TILE_SIZE_CLASS_NAMES: Record<number, string | undefined> = {
  1: styles.tile__x1,
  2: styles.tile__x2,
  3: styles.tile__x3,
};

export default observer(CategoriesGridTwoMBlock);
